import { useState, useEffect, useCallback, useRef } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip'
import { DynamicIcon, type IconName } from 'lucide-react/dynamic'
import {
  Loader2,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Pencil,
  Plus,
  Camera,
  ImageIcon,
} from 'lucide-react'

interface DemoInstance {
  id: number
  name: string
  status: string
  runtime_state: string
  ip_address: string | null
}

interface DevInstanceOption {
  id: number
  name: string
  is_demo: boolean
}

interface TemplateItem {
  id: number
  key: string
  name: string
  description: string
  category: string
  icon: string
  source_repo: string
  is_default: boolean
  active: boolean
  refresh_status: string
  refresh_error_log: string | null
  last_refreshed_at: string | null
  demo_instance: DemoInstance | null
  screenshot_url: string | null
}

interface CaptureState {
  template_id?: number
  status: string
  error?: string
  progress?: string
}

interface AdminTemplatesIndexProps {
  templates: TemplateItem[]
  dev_instances: DevInstanceOption[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Index({ templates, dev_instances = [] }: AdminTemplatesIndexProps) {
  const { t } = useTranslation('admin')
  const [refreshTarget, setRefreshTarget] = useState<TemplateItem | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const [captureTarget, setCaptureTarget] = useState<TemplateItem | null>(null)
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>('')
  const [captureState, setCaptureState] = useState<CaptureState>({ status: 'idle' })
  const [isSubmittingCapture, setIsSubmittingCapture] = useState(false)
  const prevCaptureStatusRef = useRef<string>('idle')

  // Poll capture status
  const pollCaptureStatus = useCallback(() => {
    fetch('/admin/templates/capture_status', {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
    })
      .then(res => res.json())
      .then((data: CaptureState) => {
        const wasCapturing = prevCaptureStatusRef.current === 'capturing'
        prevCaptureStatusRef.current = data.status
        setCaptureState(data)
        if (wasCapturing && data.status !== 'capturing') {
          router.reload({ only: ['templates'] })
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    pollCaptureStatus()
  }, [pollCaptureStatus])

  // Auto-refresh when capturing or refreshing
  useEffect(() => {
    const needsRefresh = captureState.status === 'capturing' ||
      templates.some(
        tpl => tpl.refresh_status === 'refreshing' ||
          (tpl.demo_instance && ['provisioning', 'setup_in_progress', 'pending'].includes(tpl.demo_instance.status))
      )
    if (needsRefresh) {
      const interval = setInterval(() => {
        pollCaptureStatus()
        if (captureState.status !== 'capturing') {
          router.reload({ only: ['templates'] })
        }
      }, 10000)
      return () => clearInterval(interval)
    }
  }, [templates, captureState.status, pollCaptureStatus])

  const handleRefresh = (template: TemplateItem) => {
    setRefreshTarget(template)
  }

  const confirmRefresh = () => {
    if (!refreshTarget) return
    setIsRefreshing(true)
    router.post(`/admin/templates/${refreshTarget.id}/refresh`, {}, {
      onFinish: () => {
        setIsRefreshing(false)
        setRefreshTarget(null)
      }
    })
  }

  const handleCaptureScreenshot = (template: TemplateItem) => {
    setCaptureTarget(template)
    setSelectedInstanceId('')
  }

  const isBatchCapture = captureTarget?.id === -1

  const confirmCapture = () => {
    if (!captureTarget || !selectedInstanceId) return
    setIsSubmittingCapture(true)

    const url = isBatchCapture
      ? '/admin/templates/capture_all_screenshots'
      : `/admin/templates/${captureTarget.id}/capture_screenshot`

    router.post(url, {
      dev_instance_id: selectedInstanceId,
    }, {
      onFinish: () => {
        setIsSubmittingCapture(false)
        setCaptureTarget(null)
        pollCaptureStatus()
      }
    })
  }

  const isCaptureInProgress = captureState.status === 'capturing'

  const getDemoStatus = (template: TemplateItem) => {
    const instance = template.demo_instance
    if (!instance) {
      return <Badge variant="outline" className="text-amber-600 border-amber-300">{t('templates.demoStatus.noDemo')}</Badge>
    }
    if (['provisioning', 'setup_in_progress', 'pending'].includes(instance.status)) {
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300">
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
          {t('templates.demoStatus.creating')}
        </Badge>
      )
    }
    if (instance.status === 'active') {
      return <Badge variant="outline" className="text-green-600 border-green-300">{t('templates.demoStatus.active')}</Badge>
    }
    if (instance.status === 'setup_failed') {
      return <Badge variant="destructive">{t('templates.demoStatus.failed')}</Badge>
    }
    return <Badge variant="outline">{instance.status}</Badge>
  }

  const getRefreshStatus = (template: TemplateItem) => {
    if (template.refresh_status === 'refreshing') {
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300">
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
          {t('templates.refreshStatus.refreshing')}
        </Badge>
      )
    }
    if (template.refresh_status === 'failed') {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="destructive">
                <AlertCircle className="h-3 w-3 mr-1" />
                {t('templates.refreshStatus.failed')}
              </Badge>
            </TooltipTrigger>
            {template.refresh_error_log && (
              <TooltipContent className="max-w-sm">
                <p className="text-xs">{template.refresh_error_log}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )
    }
    if (template.last_refreshed_at) {
      return (
        <span className="text-xs text-muted-foreground">
          {new Date(template.last_refreshed_at).toLocaleDateString()}
        </span>
      )
    }
    return <span className="text-xs text-muted-foreground">&mdash;</span>
  }

  const getScreenshotStatus = (template: TemplateItem) => {
    if (isCaptureInProgress && (captureState.template_id === template.id || captureState.template_id === null)) {
      const isCurrentTemplate = captureState.template_id === template.id
      if (isCurrentTemplate) {
        return (
          <Badge variant="outline" className="text-blue-600 border-blue-300">
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
            {captureState.progress ? `${t('templates.screenshot.capturing')} (${captureState.progress})` : t('templates.screenshot.capturing')}
          </Badge>
        )
      }
    }
    if (captureState.status === 'failed' && captureState.template_id === template.id) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="destructive">
                <AlertCircle className="h-3 w-3 mr-1" />
                {t('templates.screenshot.failed')}
              </Badge>
            </TooltipTrigger>
            {captureState.error && (
              <TooltipContent className="max-w-sm">
                <p className="text-xs">{captureState.error}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )
    }
    if (template.screenshot_url) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="outline" className="text-green-600 border-green-300">
                <ImageIcon className="h-3 w-3 mr-1" />
                {t('templates.screenshot.hasScreenshot')}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="left" className="p-0">
              <img src={template.screenshot_url} alt={template.name} className="w-48 h-auto rounded" />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }
    return <span className="text-xs text-muted-foreground">&mdash;</span>
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('templates.title') }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{t('templates.title')}</h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('templates.description')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setCaptureTarget({ id: -1 } as TemplateItem); setSelectedInstanceId('') }}
                    disabled={isCaptureInProgress || templates.every(t => t.screenshot_url)}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    {t('templates.captureAll.button')}
                  </Button>
                  <Button asChild>
                    <Link href="/admin/templates/new">
                      <Plus className="h-4 w-4 mr-2" />
                      {t('templates.newTemplate')}
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Templates Table */}
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('templates.table.name')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('templates.table.sourceRepo')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('templates.table.demoStatus')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('templates.table.screenshot')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('templates.table.refreshStatus')}
                        </th>
                        <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                          {t('templates.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {templates.map((template) => {
                        const isRefreshDisabled = template.refresh_status === 'refreshing' ||
                          (template.demo_instance != null && ['provisioning', 'setup_in_progress', 'pending'].includes(template.demo_instance.status))

                        return (
                          <tr key={template.id} className="hover:bg-muted/50">
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted shrink-0">
                                  <DynamicIcon name={template.icon as IconName} className="h-4 w-4 text-muted-foreground" />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm">{template.name}</span>
                                    {template.is_default && (
                                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{t('templates.default')}</Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground truncate">{template.key}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{template.source_repo}</code>
                            </td>
                            <td className="px-3 py-2">{getDemoStatus(template)}</td>
                            <td className="px-3 py-2">{getScreenshotStatus(template)}</td>
                            <td className="px-3 py-2">{getRefreshStatus(template)}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-end gap-1">
                                {template.demo_instance?.status === 'active' && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    aria-label="Open demo"
                                    asChild
                                  >
                                    <a
                                      href={`https://${template.demo_instance.name}.sandbox.cayu.app`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                    </a>
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  aria-label="Take screenshot"
                                  onClick={() => handleCaptureScreenshot(template)}
                                  disabled={isCaptureInProgress}
                                >
                                  <Camera className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  aria-label="Refresh"
                                  onClick={() => handleRefresh(template)}
                                  disabled={isRefreshDisabled}
                                >
                                  {isRefreshDisabled ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  aria-label="Edit"
                                  asChild
                                >
                                  <Link href={`/admin/templates/${template.id}/edit`}>
                                    <Pencil className="h-4 w-4" />
                                  </Link>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Refresh Confirmation Dialog */}
      <AlertDialog open={!!refreshTarget} onOpenChange={(open) => !open && setRefreshTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('templates.refresh.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('templates.refresh.description', { name: refreshTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRefreshing}>{t('templates.refresh.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRefresh} disabled={isRefreshing}>
              {isRefreshing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('templates.refresh.refreshing')}
                </>
              ) : (
                t('templates.refresh.confirm')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Capture Screenshot Dialog */}
      <Dialog open={!!captureTarget} onOpenChange={(open) => !open && setCaptureTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isBatchCapture ? t('templates.captureAll.title') : t('templates.capture.title')}</DialogTitle>
            <DialogDescription>
              {isBatchCapture
                ? t('templates.captureAll.description', { count: templates.filter(t => !t.screenshot_url).length })
                : t('templates.capture.description', { name: captureTarget?.name })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('templates.capture.selectInstance')}</label>
              <Select value={selectedInstanceId} onValueChange={setSelectedInstanceId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('templates.capture.instancePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {dev_instances.map((instance) => (
                    <SelectItem key={instance.id} value={String(instance.id)}>
                      {instance.name}{instance.is_demo && <span className="ml-1 text-xs text-violet-600">(Demo)</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dev_instances.length === 0 && (
                <p className="text-xs text-destructive">{t('templates.capture.noInstances')}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCaptureTarget(null)} disabled={isSubmittingCapture}>
              {t('templates.capture.cancel')}
            </Button>
            <Button onClick={confirmCapture} disabled={isSubmittingCapture || !selectedInstanceId}>
              {isSubmittingCapture ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('templates.capture.starting')}
                </>
              ) : (
                <>
                  <Camera className="h-4 w-4 mr-2" />
                  {t('templates.capture.confirm')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
